import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaArrowLeft, FaTools, FaExclamationTriangle, FaUserFriends, FaEye, FaTimes, FaWrench } from 'react-icons/fa';
import AssistantSidebar from '../components/AssistantSidebar';
import { API_ENDPOINTS } from '../config/api';
import '../styles/AssetHistoryPage.css';

const AssetHistoryPage = () => {
  const { assetId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [historyData, setHistoryData] = useState(null);
  const [activeTab, setActiveTab] = useState('units');
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);

  const fetchAssetHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const token = sessionStorage.getItem('token');
      const response = await axios.get(
        `${API_ENDPOINTS.ASSETS}/${assetId}/history`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setHistoryData(response.data.data);
      // Set first unit as selected by default
      if (response.data.data.asset.units && response.data.data.asset.units.length > 0) {
        setSelectedUnit(response.data.data.asset.units[0]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch asset history');
      console.error('Error fetching asset history:', err);
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchAssetHistory();
  }, [assetId, fetchAssetHistory]);

  const calculateDays = (fromDate, toDate) => {
    if (!fromDate) return 0;
    const from = new Date(fromDate);
    const to = toDate ? new Date(toDate) : new Date();
    const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDuration = (duration) => {
    if (!duration) return 'N/A';
    return duration
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getAssignmentStatusLabel = (status) => {
    if (status === 'active') return 'Assigned';
    if (status === 'returned') return 'Released';
    if (status === 'swapped') return 'Swapped';
    return status;
  };

  const getUnitStatusCounts = () => {
    if (!historyData?.asset?.units) return { available: 0, allocated: 0, others: 0 };
    const units = historyData.asset.units;
    let available = 0;
    let allocated = 0;
    let others = 0;
    
    units.forEach(unit => {
      if (unit.status === 'available' || unit.status === 'not_assigned') {
        available++;
      } else if (unit.status === 'allocated' || unit.assignedTo) {
        allocated++;
      } else {
        others++;
      }
    });
    
    return { available, allocated, others };
  };

  const handleViewDetails = (assignment) => {
    setSelectedAssignment(assignment);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="asset-history-container">
        <AssistantSidebar />
        <div className="asset-history-content">
          <div className="loading">Loading asset history...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="asset-history-container">
        <AssistantSidebar />
        <div className="asset-history-content">
          <div className="error-message">{error}</div>
          <button onClick={() => navigate(-1)} className="btn-back">
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!historyData) {
    return (
      <div className="asset-history-container">
        <AssistantSidebar />
        <div className="asset-history-content">
          <div>No data found</div>
        </div>
      </div>
    );
  }

  const asset = historyData.asset;
  const assignments = historyData.assignments || [];
  const maintenanceLogs = historyData.maintenanceLogs || [];
  const issues = historyData.issues || [];
  const summary = historyData.summary || {};

  return (
    <div className="asset-history-container">
      <AssistantSidebar />
      <div className="asset-history-content">
        <div className="history-header">
          <button onClick={() => navigate(-1)} className="btn-back">
            <FaArrowLeft /> Back
          </button>
          <h1>{asset.assetName} - Complete History</h1>
        </div>

        {/* Asset Metadata Card */}
        <div className="asset-metadata-card">
          <div className="metadata-grid">
            <div className="metadata-item">
              <span className="label">Asset ID:</span>
              <span className="value">{asset._id}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Asset Name:</span>
              <span className="value">{asset.assetName}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Serial Number:</span>
              <span className="value">{asset.serialNumber || 'N/A'}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Category:</span>
              <span className="value">{asset.category?.categoryName || asset.category}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Department:</span>
              <span className="value">{asset.department?.departmentName || asset.department}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Status:</span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {asset.units && asset.units.length > 0 && (() => {
                  const counts = getUnitStatusCounts();
                  return (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {counts.available > 0 && (
                        <span className="unit-allocation-badge available" title="Available units">
                          {counts.available} <strong>available</strong>
                        </span>
                      )}
                      {counts.allocated > 0 && (
                        <span className="unit-allocation-badge allocated" title="Allocated units">
                          {counts.allocated} <strong>allocated</strong>
                        </span>
                      )}
                      {counts.others > 0 && (
                        <span className="unit-allocation-badge others" title="Other status">
                          {counts.others} <strong>other</strong>
                        </span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className="metadata-item">
              <span className="label">Condition:</span>
              <span className="value">{asset.condition || 'N/A'}</span>
            </div>
            <div className="metadata-item">
              <span className="label">Type:</span>
              <span className="value">{asset.assetType}</span>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card">
            <div className="icon"><FaUserFriends /></div>
            <div className="info">
              <h3>{summary.totalAssignments || 0}</h3>
              <p>Users Assigned</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="icon"><FaWrench /></div>
            <div className="info">
              <h3>{summary.maintenance?.preventive || 0}</h3>
              <p>Preventive Maintenance</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="icon"><FaTools /></div>
            <div className="info">
              <h3>{summary.maintenance?.breakdown || 0}</h3>
              <p>Breakdowns</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="icon"><FaWrench /></div>
            <div className="info">
              <h3>{summary.maintenance?.calibration || 0}</h3>
              <p>Calibration</p>
            </div>
          </div>
          <div className="summary-card">
            <div className="icon"><FaExclamationTriangle /></div>
            <div className="info">
              <h3>{summary.issues?.total || 0}</h3>
              <p>Issues Reported</p>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="tabs-nav">
          <button
            className={`tab-btn ${activeTab === 'units' ? 'active' : ''}`}
            onClick={() => setActiveTab('units')}
          >
            All Units {(() => {
              const counts = getUnitStatusCounts();
              const parts = [];
              if (counts.available > 0) parts.push(`${counts.available} Available`);
              if (counts.allocated > 0) parts.push(`${counts.allocated} Allocated`);
              if (counts.others > 0) parts.push(`${counts.others} Other`);
              return parts.length > 0 ? `(${parts.join(', ')})` : `(${historyData.asset.units?.length || 0})`;
            })()}
          </button>
          <button
            className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
            onClick={() => setActiveTab('assignments')}
          >
            Assignment History ({assignments.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'maintenance' ? 'active' : ''}`}
            onClick={() => setActiveTab('maintenance')}
          >
            Maintenance Logs ({maintenanceLogs.length})
          </button>
          <button
            className={`tab-btn ${activeTab === 'issues' ? 'active' : ''}`}
            onClick={() => setActiveTab('issues')}
          >
            Issues ({issues.length})
          </button>
        
        </div>

        {/* Units Tab */}
        {activeTab === 'units' && (
          <div className="tab-content">
            <h2>Available Units</h2>
            
            {historyData.asset.units && historyData.asset.units.length > 0 ? (
              <div className="units-grid">
                {historyData.asset.units.map((unit, idx) => (
                  <div 
                    key={idx} 
                    className={`unit-card ${selectedUnit?.unitId === unit.unitId ? 'selected' : ''} unit-status-${unit.status}`}
                    onClick={() => setSelectedUnit(unit)}
                  >
                    <div className="unit-id">{unit.unitId}</div>
                    <div className="unit-info">
                      <p><strong>Status:</strong> <span className={`status-badge status-${unit.status}`}>{unit.status}</span></p>
                      <p><strong>Assigned:</strong> {unit.assignedTo ? 'Yes' : 'Not assigned'}</p>
                      <p><strong>Created:</strong> {formatDate(unit.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No units found</p>
            )}

            {selectedUnit && (
              <div className="unit-detail-section">
                <h3>Unit Details: {selectedUnit.unitId}</h3>
                <div className="unit-assignment-history">
                  <h4>Assignment History for {selectedUnit.unitId}</h4>
                  {assignments.filter(a => a.unitId === selectedUnit.unitId).length > 0 ? (
                    <div className="table-wrapper">
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Asset Name</th>
                            <th>Assigned From</th>
                            <th>Assigned Upto</th>
                            <th>Days Used</th>
                            <th>Status</th>
                            <th>User Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assignments.filter(a => a.unitId === selectedUnit.unitId).map((assignment, idx) => (
                            <tr key={idx}>
                              <td>
                                <div className="asset-name-cell">
                                  <div className="asset-name-main">{asset.assetName}</div>
                                  <div className="asset-unit-id">{selectedUnit.unitId}</div>
                                </div>
                              </td>
                              <td>{formatDate(assignment.assignedDate)}</td>
                              <td>{assignment.returnDate ? formatDate(assignment.returnDate) : 'Still in use'}</td>
                              <td className="days-used">
                                {calculateDays(assignment.assignedDate, assignment.returnDate)} days
                              </td>
                              <td>
                                <span className={`status-badge status-${assignment.status}`}>
                                  {assignment.status}
                                </span>
                              </td>
                              <td className="user-cell">
                                <strong>{assignment.userId?.firstName} {assignment.userId?.lastName}</strong>
                                <div className="user-email">{assignment.userId?.email || 'N/A'}</div>
                                <div className="user-dept">{assignment.userId?.department || 'N/A'}</div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="no-data">No assignment history for this unit</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <div className="tab-content">
            <h2>Assignment History</h2>
            {assignments.length > 0 ? (
              <div className="table-wrapper">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Asset Name</th>
                      <th>Assigned From</th>
                      <th>Assigned Upto</th>
                      <th>Days Used</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((assignment, idx) => (
                      <tr key={idx}>
                        <td className="asset-name">
                          <div className="asset-name-cell">
                            <div className="asset-name-main">{asset.assetName}</div>
                            <div className="asset-unit-id">{assignment.unitId || 'N/A'}</div>
                          </div>
                        </td>
                        <td>{formatDate(assignment.assignedDate)}</td>
                        <td>{assignment.returnDate ? formatDate(assignment.returnDate) : 'Still in use'}</td>
                        <td className="days-used">
                          {calculateDays(assignment.assignedDate, assignment.returnDate)} days
                        </td>
                        <td>
                          <span className={`status-badge status-${assignment.status}`}>
                            {assignment.status}
                          </span>
                        </td>
                         <td>
                                <button 
                                  className="btn-view-details"
                                  onClick={() => handleViewDetails(assignment)}
                                  title="View allocation details"
                                >
                                  <FaEye /> View Details
                                </button>
                              </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No assignment history found</p>
            )}
          </div>
        )}

        {/* Maintenance Tab */}
        {activeTab === 'maintenance' && (
          <div className="tab-content">
            <h2>Maintenance Schedule & Logs</h2>
            {maintenanceLogs.length > 0 ? (
              <div className="table-wrapper">
                <table className="history-table">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Reported Date</th>
                      <th>Department</th>
                      <th>Priority</th>
                      <th>Sent To</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {maintenanceLogs.map((log, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`maintenance-type maintenance-${log.maintenanceType}`}>
                            {log.maintenanceType}
                          </span>
                        </td>
                        <td>{formatDate(log.reportedDate)}</td>
                        <td>{log.department || 'N/A'}</td>
                        <td>
                          <span className={`priority priority-${log.priorityLevel || 'medium'}`}>
                            {log.priorityLevel || 'medium'}
                          </span>
                        </td>
                        <td>{log.sentTo || 'Internal'}</td>
                        <td>
                          <span className={`log-status`}>
                            {log.status || 'pending'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">No maintenance logs found</p>
            )}
          </div>
        )}

        {/* Issues Tab */}
        {activeTab === 'issues' && (
          <div className="tab-content">
            <h2>Issues & Breakdowns</h2>
            {issues.length > 0 ? (
              <div className="issues-list">
                {issues.map((issue, idx) => (
                  <div key={idx} className="issue-card">
                    <div className="issue-header">
                      <span className={`issue-type issue-${issue.issueType}`}>
                        {issue.issueType}
                      </span>
                      <span className={`severity severity-${issue.severity || 'medium'}`}>
                        {issue.severity}
                      </span>
                      <span className={`issue-status issue-status-${issue.status}`}>
                        {issue.status}
                      </span>
                    </div>
                    <div className="issue-details">
                      <p><strong>Description:</strong> {issue.description}</p>
                      <p><strong>Required Skill:</strong> {issue.requiredSkill}</p>
                      <p><strong>Department:</strong> {issue.department}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="no-data">No issues found</p>
            )}
          </div>
        )}

        {/* User Details Tab */}
    

        {/* Details Modal */}
        {showDetailsModal && selectedAssignment && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-details">
                <h2>Asset Allocation Details</h2>
                <button 
                  className="btn-close-modal"
                  onClick={() => setShowDetailsModal(false)}
                  title="Close"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="modal-body">
                {/* Asset Details Section */}
                <div className="details-section">
                  <h3 className="section-title">Asset Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Asset Title:</span>
                      <span className="detail-value">{asset.assetName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Asset ID:</span>
                      <span className="detail-value">{asset._id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Serial Number:</span>
                      <span className="detail-value">{asset.serialNumber || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Unit ID:</span>
                      <span className="detail-value">{selectedAssignment.unitId || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Category:</span>
                      <span className="detail-value">{asset.category?.categoryName || asset.category}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Brand/Model:</span>
                      <span className="detail-value">{asset.brand || 'N/A'} {asset.model || ''}</span>
                    </div>
                  </div>
                </div>

                {/* User Details Section */}
                <div className="details-section">
                  <h3 className="section-title">User Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">User Name:</span>
                      <span className="detail-value">{selectedAssignment.userId?.firstName} {selectedAssignment.userId?.lastName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email:</span>
                      <span className="detail-value">{selectedAssignment.userId?.email || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Department:</span>
                      <span className="detail-value">{selectedAssignment.userId?.department?.departmentName || selectedAssignment.userId?.department || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Desk Location:</span>
                      <span className="detail-value">{selectedAssignment.deskLocation || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                {/* Allocation History Section */}
                <div className="details-section">
                  <h3 className="section-title">Allocation History</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Asset Requested Date:</span>
                      <span className="detail-value">{formatDate(selectedAssignment.assetRequest?.createdAt) || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Asset Allocated Date:</span>
                      <span className="detail-value">{formatDate(selectedAssignment.assignedDate)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Allocation Period:</span>
                      <span className="detail-value">{formatDuration(selectedAssignment.assetRequest?.duration)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Quantity Assigned:</span>
                      <span className="detail-value">{selectedAssignment.assignedQuantity || 1} unit(s)</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Current Status:</span>
                      <span className="detail-value">
                        <span className={`status-badge status-${selectedAssignment.status}`}>
                          {getAssignmentStatusLabel(selectedAssignment.status)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Release History Section */}
                {selectedAssignment.returnDate && (
                  <div className="details-section">
                    <h3 className="section-title">Release History</h3>
                    <div className="details-grid">
                      <div className="detail-item">
                        <span className="detail-label">Asset Release Request Date:</span>
                        <span className="detail-value">{formatDate(selectedAssignment.assetRequest?.createdAt) || 'N/A'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Asset Released Date:</span>
                        <span className="detail-value">{formatDate(selectedAssignment.returnDate)}</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Days Used:</span>
                        <span className="detail-value">{calculateDays(selectedAssignment.assignedDate, selectedAssignment.returnDate)} days</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Release Status:</span>
                        <span className="detail-value">
                          <span className={`status-badge status-${selectedAssignment.status}`}>
                            {getAssignmentStatusLabel(selectedAssignment.status)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {selectedAssignment.remarks && (
                  <div className="details-section">
                    <h3 className="section-title">Remarks</h3>
                    <p className="remarks-text">{selectedAssignment.remarks}</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetHistoryPage;
